/**
 * 業務詳細（`/businesses/$businessId`）— 業務 1 件の処理を、ロジック図 1 枚で描く。
 *
 * **図は Figma の図の形にした**（Dhik 2026-10-01「データの粒度、表示の両観点で見づらいという声をもらった。
 * 下記の図のようになっていればわかりやすい」）。節点は 入力 ・ 業務ルール ・ 業務ルールの出力 ・ 分岐 ・
 * 作用 ・ 終了 の 6 種類で、形ではなく箱の上端のタグで見分ける。結末を決める判定表は業務ルールの箱に
 * せず、入力の列ごとの分岐に開いて条件を線のラベルに出す。**節点は図より増やさない**（Dhik 2026-10-01
 * 「なんで図よりノード増やしているんだよ」）— 業務ルールの中身の表は図に入れず箱を押して開き、出力を
 * 分岐だけが読む判定表は分岐に畳む。材料から図を組む規則は `business-logic-diagram/logic-from-rules.ts`。
 * 材料は読取 `getBusinessRules` で、画面と MCP の両方がこの同じ読取で読む（新しい読取は足していない）。
 *
 * **業務ルール（と作用）の箱を押すと業務ルールのモーダルを開く**（`RuleModal`）。右パネルは置かない。
 *
 * **図の枠は共有の枠（`flow-workspace/`）に載せる。** 全画面・左右比較の既定 2 面（`?compare=0` で
 * 変更後 1 面。節点にも業務ルールにも差が無ければ 1 面で、比較のボタンは押せない）・2 面の視点の同期は
 * 枠が持つ。
 *
 * **案件を選んでいるときは master と案件適用後の 2 回引いて突き合わせる。** 2 面は変更前と変更後を
 * 合わせた材料で置き場所を 1 つに決めて描き、片方にしか無い箱にその面の色の枠を付ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件を、入力・業務ルール・業務ルールの出力・分岐・作用・終了の 6 種の節点と条件つきの線で描き、業務ルールを押すとモーダルで中身を見せ、案件を選んでいれば変更前と変更後を並べて印を重ねる。
 * @graph-connects useBusinessRules [calls] 業務 1 件の業務ロジックの取得
 * @graph-connects buildLogicModelFromRules [calls] 業務ロジックからロジック図の材料を組む
 * @graph-connects buildLogicDiagram [calls] 図 1 面ぶんの節点と線
 * @graph-connects FlowWorkspace [calls] 図・全画面・左右比較の枠
 * @graph-connects RuleModal [calls] 押した業務ルールのモーダル
 */
import type { GetBusinessRulesOutput } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeletedInPjState } from '../components/spec/DeletedInPjState.js';
import { DetailSection } from '../components/spec/DetailSection.js';
import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { useBusinessRules } from '../hooks/queries/spec-graph-rule-flow-queries.js';
import { resolvePjDeletion } from '../lib/pj-deleted-node.js';
import {
  buildRuleListRows,
  countRuleChanges,
  RuleModal,
  type RuleListBefore,
} from './business-logic-detail.js';
import { buildLogicDiagram, LOGIC_NODE_TYPES } from './business-logic-diagram/logic-build.js';
import { buildLogicModelFromRules } from './business-logic-diagram/logic-from-rules.js';
import { computeLogicLayout } from './business-logic-diagram/logic-layout.js';
import {
  logicKeysOnlyIn,
  mergeLogicModels,
  type LogicDiagramModel,
} from './business-logic-diagram/logic-model.js';
import {
  useLogicFromRulesTexts,
  useLogicKindLabels,
} from './business-logic-diagram/logic-texts.js';
import { untranslatedHoverText } from './business-hover-title.js';
import { FlowCanvas } from './flow-workspace/flow-canvas.js';
import { useCompareSearch } from './flow-workspace/use-compare-search.js';
import { FlowWorkspace } from './flow-workspace/flow-workspace.js';
import { pjLinkSearch } from './pj-link.js';
import { BUSINESS_MAP_ROUTE } from './route-paths.js';
import { FlowInfoMark } from './story-flow/flow-info-mark.js';
import { BusinessDetailLink } from './view-links/business-logic.js';
import { pjLinkValue } from './view-links/common.js';

/**
 * `BusinessDataflowPage` の props。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business データの流れの画面が受け取る業務の番号・案件の番号・マスターと案件を重ねた姿に業務が居るか
 * @graph-connects none
 */
export interface BusinessDataflowPageProps {
  /** 開く業務の番号。 */
  businessId: number;
  /** 重ねる案件の番号。マスターを読むなら省く。 */
  pjNumber?: number;
  /** 案件が新設した業務で、マスターには居ないか（居ないならマスターを引かない）。 */
  masterAbsent?: boolean;
  /** 案件が消す業務で、案件を重ねた姿には居ないか（居ないなら案件を重ねた姿を引かない）。 */
  appliedAbsent?: boolean;
}

/**
 * データの流れの画面を、取得中・未存在・案件が削除・失敗・本体の 5 状態で描く。
 *
 * @param props 業務の番号・案件の番号・マスターと案件を重ねた姿に業務が居ないか
 * @returns データの流れの画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件のデータの流れの図を、取得中・未存在・案件が削除・失敗・本体の 5 状態で描く。
 * @graph-connects useBusinessRules [calls] 業務 1 件の業務ロジックの取得
 * @graph-connects resolvePjDeletion [calls] 変更後の未存在が案件の削除かの判定
 * @graph-connects DeletedInPjState [calls] 案件が消した業務の変更前と、変更後に無いことの左右比較
 */
export function BusinessDataflowPage({
  businessId,
  pjNumber,
  masterAbsent = false,
  appliedAbsent = false,
}: BusinessDataflowPageProps) {
  const { t } = useTranslation();
  const master = useBusinessRules(businessId, undefined, !masterAbsent);
  const after = useBusinessRules(businessId, pjNumber, !appliedAbsent);
  const active = pjNumber === undefined ? master : after;

  const backLink = (
    <Link
      to={BUSINESS_MAP_ROUTE}
      {...pjLinkSearch(pjLinkValue(pjNumber))}
      className="text-sm text-[var(--color-accent-primary)] underline"
    >
      {t('spec.backToList')}
    </Link>
  );
  const skeleton = (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
      <Skeleton variant="rectangular" height="6rem" />
      <Skeleton variant="rectangular" height="24rem" />
    </div>
  );

  const deletion = resolvePjDeletion({
    pjMode: pjNumber !== undefined && !masterAbsent,
    afterError: active.error,
    afterKnownMissing: pjNumber !== undefined && appliedAbsent,
    before: master,
  });
  if (deletion.kind === 'absent')
    return <NotFoundState id={String(businessId)} action={backLink} />;
  if (deletion.kind === 'beforeFailed') return <ErrorState error={deletion.error} />;
  if (deletion.kind === 'deleted' && master.data !== undefined) {
    return (
      <DeletedInPjState
        nodeType="Business"
        id={String(businessId)}
        heading={displayLabel(master.data.label, t, master.data.untranslated)}
        pjNumber={String(pjNumber)}
        action={backLink}
        before={
          <BusinessDataflowBody
            logic={master.data}
            before={null}
            pjNumber={undefined}
            showHeading={false}
          />
        }
      />
    );
  }
  if (deletion.kind === 'waiting') return skeleton;
  if (active.error !== null && active.error !== undefined)
    return <ErrorState error={active.error} />;
  if (active.isLoading || active.data === undefined) return skeleton;
  /* 案件モードの変更前が届くまで待つ。先に描くと、印の無い図が一瞬出てから入れ替わる。 */
  if (pjNumber !== undefined && !masterAbsent && master.data === undefined) return skeleton;

  const before: RuleListBefore =
    pjNumber === undefined ? null : masterAbsent ? 'missing' : (master.data ?? null);
  return <BusinessDataflowBody logic={active.data} before={before} pjNumber={pjNumber} />;
}

/** `BusinessDataflowBody` の props。 @graph-connects none */
interface BusinessDataflowBodyProps {
  /** 描く業務ロジック（案件を選んでいれば変更後）。 */
  logic: GetBusinessRulesOutput;
  /** 変更前。案件を選んでいなければ null。 */
  before: RuleListBefore;
  /** 重ねている案件の番号。選んでいなければ undefined。 */
  pjNumber: number | undefined;
  /**
   * 型の札と業務名の見出しを出すか。省けば出す。案件が消した業務の変更前は、削除の器
   * （`DeletedInPjState`）が見出しを 1 回だけ出すので、左の面の中では出さない。
   */
  showHeading?: boolean;
}

/** 空のときの 1 行の文の見た目。 @graph-connects none */
const EMPTY_CLASS = 'text-sm text-[var(--color-text-muted)]';

/**
 * 取得済みの業務ロジックから、見出し・図・業務ルールのモーダルを組む。
 *
 * @param props 業務ロジック・変更前・案件番号
 * @returns データの流れの本体
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得済みの業務ロジックから骨組みと置き場所を作り、型の札と業務名の見出し・図の 1 面か 2 面・業務ルールを押したときのモーダルを描く。
 * @graph-connects NodeTypeBadge [calls] 見出しの左上の型の札
 * @graph-connects buildLogicModelFromRules [calls] 業務ロジックからロジック図の材料を組む
 * @graph-connects computeLogicLayout [calls] 図の置き場所
 * @graph-connects buildLogicDiagram [calls] 図 1 面ぶんの節点と線
 * @graph-connects useLogicFromRulesTexts [calls] 実データの図の文言
 * @graph-connects useLogicKindLabels [calls] 種類のタグの文言
 * @graph-connects useCompareSearch [calls] 左右比較の開閉
 * @graph-connects FlowInfoMark [calls] 図の枠の ⓘ
 * @graph-connects dataflowInfoLines [calls] 図の使い方と凡例の ⓘ の行
 * @graph-connects RuleModal [calls] 業務ルールのモーダル
 */
function BusinessDataflowBody({
  logic,
  before,
  pjNumber,
  showHeading = true,
}: BusinessDataflowBodyProps) {
  const { t } = useTranslation();
  const [openRuleId, setOpenRuleId] = useState<number | null>(null);
  const pjMode = pjNumber !== undefined;

  const logicTexts = useLogicFromRulesTexts();
  const kindLabels = useLogicKindLabels();
  const afterModel = useMemo(
    () => buildLogicModelFromRules(logic, logicTexts),
    [logic, logicTexts],
  );
  const beforeModel = useMemo(
    () =>
      before === null || before === 'missing' ? null : buildLogicModelFromRules(before, logicTexts),
    [before, logicTexts],
  );
  const rows = useMemo(() => buildRuleListRows(logic, before), [logic, before]);
  const counts = countRuleChanges(rows);
  const onlyHere = useMemo(
    () =>
      pjMode
        ? {
            before:
              beforeModel === null ? new Set<string>() : logicKeysOnlyIn(beforeModel, afterModel),
            after:
              beforeModel === null
                ? new Set(afterModel.nodes.map((node) => node.key))
                : logicKeysOnlyIn(afterModel, beforeModel),
          }
        : null,
    [pjMode, beforeModel, afterModel],
  );
  /*
   * 業務ルールにも節点にも差が無ければ、同じ図が 2 枚並ぶだけになる。比較は開かせない。
   * 案件を選んでいなければ差は無いので、左右に割るのは案件モードに限られる。
   */
  const hasDiff =
    counts.created + counts.updated + counts.deleted > 0 ||
    (onlyHere?.before.size ?? 0) + (onlyHere?.after.size ?? 0) > 0;
  const compare = useCompareSearch(hasDiff);
  const { split, disabled, toggle } = compare;

  /* 2 面は変更前と変更後を合わせた材料で置き場所を 1 つに決め、同じ置き場所で描く。 */
  const layoutModel = useMemo(
    () => (split && beforeModel !== null ? mergeLogicModels(beforeModel, afterModel) : afterModel),
    [split, beforeModel, afterModel],
  );
  const layout = useMemo(() => computeLogicLayout(layoutModel), [layoutModel]);

  const hasRules = logic.rules.length > 0 || (beforeModel?.nodes.length ?? 0) > 0;
  const paneOf = (
    side: 'master' | 'before' | 'after',
    model: LogicDiagramModel,
    height: number,
  ) => {
    const diagram = buildLogicDiagram(model, kindLabels, {
      layoutModel,
      onSelectRule: setOpenRuleId,
      ...(side === 'master' || onlyHere === null
        ? {}
        : { diff: { side, onlyHere: onlyHere[side] } }),
    });
    return (
      <FlowCanvas
        key={side}
        side={side}
        title={t('businessDataflow.diagramTitle')}
        height={height}
        nodes={diagram.nodes}
        edges={diagram.edges}
        nodeTypes={LOGIC_NODE_TYPES}
        viewportControl={{ kind: 'own', initial: { x: 0, y: 0, zoom: 1 } }}
      />
    );
  };

  const openRow =
    openRuleId === null ? null : (rows.find((row) => row.ruleId === openRuleId) ?? null);

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      {showHeading && (
        <header className="flex flex-wrap items-center gap-3">
          <NodeTypeBadge nodeType="Business" />
          <h1
            className="text-2xl font-bold text-[var(--color-text-primary)]"
            data-id={logic.businessId}
            title={untranslatedHoverText(logic.label, logic.untranslated, t)}
          >
            <BusinessDetailLink businessId={logic.businessId} pjNumber={pjNumber}>
              {displayLabel(logic.label, t, logic.untranslated)}
            </BusinessDetailLink>
          </h1>
        </header>
      )}

      <FlowWorkspace
        layoutHeight={layout.height}
        canvasWidth={layout.width}
        {...(pjMode ? { compare: { split, disabled, onToggle: toggle, pjNumber } } : {})}
        {...(hasRules
          ? {}
          : {
              placeholder: (
                <DetailSection title={t('businessDataflow.diagramTitle')}>
                  <p className={EMPTY_CLASS}>{t('businessLogic.rulesEmpty')}</p>
                </DetailSection>
              ),
            })}
        info={<FlowInfoMark lines={dataflowInfoLines(t, pjMode)} />}
        panes={({ height }) =>
          split
            ? [
                beforeModel === null ? (
                  <section key="before" data-compare-pane="before">
                    <p className={EMPTY_CLASS}>{t('businessDataflow.status.absent')}</p>
                  </section>
                ) : (
                  paneOf('before', beforeModel, height)
                ),
                paneOf('after', afterModel, height),
              ]
            : paneOf(pjMode ? 'after' : 'master', afterModel, height)
        }
      />

      {openRow !== null && (
        <RuleModal
          row={openRow}
          pjNumber={pjNumber}
          split={split}
          onToggleCompare={toggle}
          onClose={() => {
            setOpenRuleId(null);
          }}
          listedRuleIds={new Set(rows.map((row) => row.ruleId))}
          onOpenRule={setOpenRuleId}
        />
      )}
    </div>
  );
}

/**
 * 図の枠の ⓘ に束ねる行 — 使い方と凡例。案件モードでだけ意味を持つもの（増えた ・ 消えた）は案件
 * モードでだけ載せる。形を覚えた人には要らないので、図の上に常に並べない。
 *
 * @param t 訳
 * @param pjMode 案件モードか
 * @returns ⓘ に出す行
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の使い方と凡例（6 種の節点、案件モードなら増えた消えた）を ⓘ に出す行にする。
 * @graph-connects none
 */
function dataflowInfoLines(t: (key: string) => string, pjMode: boolean): string[] {
  const legend = ['input', 'rule', 'ruleOutput', 'branch', 'effect', 'end'];
  if (pjMode) legend.push('added', 'removed');
  return [
    t('businessDataflow.status.idle'),
    ...legend.map((key) => `・${t(`logicDiagram.real.legend.${key}`)}`),
  ];
}
